import {
  Box,
  Button,
  Center,
  createIcon,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  IconProps,
  Image,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Tag,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import ContentErrorHandler from 'components/ContentErrorHandler';
import { FL_QUESTIONAIRE_RESULT } from 'constants/text';
import 'font-awesome-animation/css/font-awesome-animation.min.css';
import { useRef } from 'react';
import { FaArrowDown, FaCreditCard, FaFileDownload, FaPiggyBank, FaShoppingBasket } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useGetQuery } from './api';

const QuestionaireResult = () => {
  const firstRef = useRef<null | HTMLDivElement>(null);
  const secondRef = useRef<null | HTMLDivElement>(null);
  const thridRef = useRef<null | HTMLDivElement>(null);

  const { id } = useParams();

  const { data, isFetching, isError, refetch } = useGetQuery(encodeURIComponent(id || ''), {
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });

  const handleScrollRef = (type: string) => {
    switch (type) {
      case 'firstRef':
        if (firstRef != null && firstRef.current != null) {
          firstRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;

      case 'secondRef':
        if (secondRef != null && secondRef.current != null) {
          secondRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;

      case 'thridRef':
        if (thridRef != null && thridRef.current != null) {
          thridRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;

      default:
        break;
    }
  };

  return (
    <ContentErrorHandler isLoading={isFetching} isError={isError}>
      <Stack width={'100%'} direction="column" bgGradient="linear(to-b, green.800, white)" textAlign="center">
        <Box paddingX={{ base: 3, md: 16 }} paddingY={{ base: 8, md: 16 }} color="white" w="full">
          <HStack spacing="12">
            <Flex flex={1}>
              <Box>
                <Tag variant="solid" colorScheme={'green'} marginBottom="6">
                  {FL_QUESTIONAIRE_RESULT.id + data?.questionaireId}
                </Tag>
                <Heading fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }} marginBottom={{ base: '3' }}>
                  {FL_QUESTIONAIRE_RESULT.section1Title}
                </Heading>
                <Text fontSize="sm">{FL_QUESTIONAIRE_RESULT.section1Subtitle}</Text>
              </Box>
            </Flex>
            <Flex flex={1} display={{ base: 'none', md: 'block' }}>
              <Image
                alt={'Login Image'}
                objectFit={'cover'}
                h="calc(100vh - 230px)"
                width="100%"
                src={'https://images.pexels.com/photos/6963847/pexels-photo-6963847.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'}
              />
            </Flex>
          </HStack>
          <Center mt="6">
            <FaArrowDown onClick={() => handleScrollRef('firstRef')} cursor="pointer" className="fa faa-vertical animated"></FaArrowDown>
          </Center>
        </Box>
        <Box ref={firstRef} paddingX={{ base: 3, md: 16 }} paddingY={{ base: 8, md: 16 }} background={useColorModeValue('white', 'gray.800')}>
          <Heading fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }} marginBottom={{ base: '3', md: 6 }}>
            {FL_QUESTIONAIRE_RESULT.section2Title}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: '5', md: '6' }}>
            <Box padding={{ base: 6, md: 12 }} shadow="md" backgroundColor={'whiteAlpha.100'} border="1px solid lightgray" rounded="md">
              <Center>
                <FaPiggyBank size="70px" />
              </Center>
              <Stat>
                <StatLabel>{FL_QUESTIONAIRE_RESULT.section2Saving}</StatLabel>
                <StatNumber>{data?.impact.saving}</StatNumber>
              </Stat>
              <Text fontSize="xs">{FL_QUESTIONAIRE_RESULT.section2SavingDesc}</Text>
            </Box>

            <Box padding={{ base: 6, md: 12 }} shadow="md" backgroundColor={'whiteAlpha.100'} border="1px solid lightgray" rounded="md">
              <Center>
                <FaShoppingBasket size="70px" />
              </Center>
              <Stat>
                <StatLabel>{FL_QUESTIONAIRE_RESULT.section2Expense}</StatLabel>
                <StatNumber>{data?.impact.expense}</StatNumber>
              </Stat>
              <Text fontSize="xs">{FL_QUESTIONAIRE_RESULT.section2ExpenseDesc}</Text>
            </Box>

            <Box padding={{ base: 6, md: 12 }} shadow="md" backgroundColor={'whiteAlpha.100'} border="1px solid lightgray" rounded="md">
              <Center>
                <FaCreditCard size="70px" />
              </Center>
              <Stat>
                <StatLabel>{FL_QUESTIONAIRE_RESULT.section2Debt}</StatLabel>
                <StatNumber>{data?.impact.debt}</StatNumber>
              </Stat>
              <Text fontSize="xs">{FL_QUESTIONAIRE_RESULT.section2DebtDesc}</Text>
            </Box>
          </SimpleGrid>
          <Center mt="6" display={{ base: 'none', md: 'flex' }}>
            <FaArrowDown onClick={() => handleScrollRef('secondRef')} cursor="pointer" className="fa faa-vertical animated"></FaArrowDown>
          </Center>
        </Box>
        <Box ref={secondRef} paddingX={{ base: 3, md: 16 }} paddingY={{ base: 8, md: 16 }}>
          <Heading color="blackAlpha.900" fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }} marginBottom="3">
            Saran dari kami
          </Heading>
          <VStack px={{ base: '12', md: '20%' }}>
            {data?.suggestionMessage.map((item, index) => {
              return (
                <Box key={index} padding="3" w="full" shadow="md" backgroundColor="white" border="1px solid lightgray" rounded="md">
                  <Text color="black" fontSize="sm">
                    {item}
                  </Text>
                </Box>
              );
            })}
          </VStack>
          <Center color="black" mt="6" display={{ base: 'none', md: 'flex' }}>
            <FaArrowDown onClick={() => handleScrollRef('thridRef')} cursor="pointer" className="fa faa-vertical animated"></FaArrowDown>
          </Center>
        </Box>
        {/* <Box
          ref={thridRef}
          background={useColorModeValue("white", "gray.800")}
          paddingX={{ base: 3, md: 16 }}
          paddingY={{ base: 8, md: 16 }}
        >
          <Stack align={"center"} spacing={{ base: 8, md: 10 }} direction={{ base: "column", md: "row" }}>
            <Stack textAlign={{ base: "center", md: "left" }} flex={1} spacing={{ base: 5, md: 10 }}>
              <Heading fontSize={{ base: "xl", md: "2xl", lg: "3xl" }} marginBottom={{ base: "3" }}>
                {FL_QUESTIONAIRE_RESULT.section3Title}
              </Heading>
              <Text fontSize={"sm"}>{FL_QUESTIONAIRE_RESULT.section3Subtitle}</Text>
              <Stack spacing={{ base: 4, sm: 6 }} direction={{ base: "column", sm: "row" }}>
                <Button rounded={"md"} size={"sm"} px={6} colorScheme={"blue"} leftIcon={<FaFileDownload />}>
                  {FL_QUESTIONAIRE_RESULT.section3Cta}
                </Button>
              </Stack>
            </Stack>
            <Flex flex={1} justify={"center"} align={"center"} position={"relative"} w={"full"}>
              <Blob
                w={"150%"}
                h={"150%"}
                position={"absolute"}
                top={"-20%"}
                left={0}
                zIndex={-1}
                color={useColorModeValue("red.50", "red.400")}
              />
              <Box position={"relative"} height={"300px"} rounded={"2xl"} boxShadow={"2xl"} width={"full"} overflow={"hidden"}>
                <IconButton
                  aria-label={"Play Button"}
                  variant={"ghost"}
                  _hover={{ bg: "transparent" }}
                  icon={<PlayIcon w={12} h={12} />}
                  size={"lg"}
                  color={"white"}
                  position={"absolute"}
                  left={"50%"}
                  top={"50%"}
                  transform={"translateX(-50%) translateY(-50%)"}
                />
                <Image
                  alt={"Hero Image"}
                  fit={"cover"}
                  align={"center"}
                  w={"100%"}
                  h={"100%"}
                  src={
                    "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?ixlib=rb-1.2.1&ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&auto=format&fit=crop&w=800&q=80"
                  }
                />
              </Box>
            </Flex>
          </Stack>
        </Box> */}
      </Stack>
    </ContentErrorHandler>
  );
};

const PlayIcon = createIcon({
  displayName: 'PlayIcon',
  viewBox: '0 0 58 58',
  d: 'M28.9999 0.562988C13.3196 0.562988 0.562378 13.3202 0.562378 29.0005C0.562378 44.6808 13.3196 57.438 28.9999 57.438C44.6801 57.438 57.4374 44.6808 57.4374 29.0005C57.4374 13.3202 44.6801 0.562988 28.9999 0.562988ZM39.2223 30.272L23.5749 39.7247C23.3506 39.8591 23.0946 39.9314 22.8332 39.9342C22.5717 39.9369 22.3142 39.8701 22.0871 39.7406C21.86 39.611 21.6715 39.4234 21.5408 39.1969C21.4102 38.9705 21.3421 38.7133 21.3436 38.4519V19.5491C21.3421 19.2877 21.4102 19.0305 21.5408 18.8041C21.6715 18.5776 21.86 18.3899 22.0871 18.2604C22.3142 18.1308 22.5717 18.064 22.8332 18.0668C23.0946 18.0696 23.3506 18.1419 23.5749 18.2763L39.2223 27.729C39.4404 27.8619 39.6207 28.0486 39.7458 28.2713C39.8709 28.494 39.9366 28.7451 39.9366 29.0005C39.9366 29.2559 39.8709 29.507 39.7458 29.7297C39.6207 29.9523 39.4404 30.1391 39.2223 30.272Z',
});

export const Blob = (props: IconProps) => {
  return (
    <Icon width={'100%'} viewBox="0 0 578 440" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M239.184 439.443c-55.13-5.419-110.241-21.365-151.074-58.767C42.307 338.722-7.478 282.729.938 221.217c8.433-61.644 78.896-91.048 126.871-130.712 34.337-28.388 70.198-51.348 112.004-66.78C282.34 8.024 325.382-3.369 370.518.904c54.019 5.115 112.774 10.886 150.881 49.482 39.916 40.427 49.421 100.753 53.385 157.402 4.13 59.015 11.255 128.44-30.444 170.44-41.383 41.683-111.6 19.106-169.213 30.663-46.68 9.364-88.56 35.21-135.943 30.551z"
        fill="currentColor"
      />
    </Icon>
  );
};

export default QuestionaireResult;
